import { FeatureFlag } from '@ps/web-core-enums';
import { Translation } from '@ps/web-shared-components';
import type {
  MultipleBetType,
  MultipleOpportunity,
  SingleOpportunity
} from '@ps/web-sports-api-types';
import classNames from 'clsx';
import React from 'react';

import useFeature from '@swf/core/hooks/useFeature';
import { useRegulatoryIntl } from '@swf/core/hooks/useRegulatoryIntl';
import {
  getIsAvailableOpportunity,
  getIsSgmInvalidOpportunity,
  getIsSgmOpportunity,
  getIsSingleAvailableOpportunity,
  getIsSingleOpportunity,
  getIsSingleUnavailableOpportunity,
  getIsSuspendedOpportunity
} from '@swf/core/models/sports/utils/betSlip/getIsOpportunity';
import { Icon } from '@swf/sports/components';
import { getMultipleName } from '@swf/sports/utils/getMultipleName';
import getRunnerNameWithHandicap from '@swf/sports/utils/getRunnerNameWithHandicap';
import { isOddsBoostMarket } from '@swf/sports/utils/isOddsBoostMarket';

import InfoTooltip from '../InfoTooltip/InfoTooltip';
import MultiplesTooltipContent from '../MultiplesTooltipContent/MultiplesTooltipContent';
import RemoveButton from '../RemoveButton/RemoveButton';

import { TXTCLI_Suspended, TXTWCL_Unavailable, TXTWCL_odds_boost } from './Heading.messages.json';
import styles from './Heading.scss';
import OddsDisplay from './components/OddsDisplay/OddsDisplay';

interface Props {
  opportunityRef: React.RefObject<HTMLDivElement>;
  opportunity: SingleOpportunity | MultipleOpportunity;
}

const Heading: React.FC<Props> = ({ opportunity, opportunityRef }) => {
  const { formatMessage } = useRegulatoryIntl();
  const isSingleOpportunity = getIsSingleOpportunity(opportunity);
  const isAvailableOpportunity = getIsAvailableOpportunity(opportunity);
  const isSingleAvailableOpportunity = getIsSingleAvailableOpportunity(opportunity);
  const isSingleUnavailableOpportunity = getIsSingleUnavailableOpportunity(opportunity);
  const isSuspendedOpportunity = getIsSuspendedOpportunity(opportunity);
  const isSgmOpportunity = getIsSgmOpportunity(opportunity);
  const isSgmInvalidOpportunity = getIsSgmInvalidOpportunity(opportunity);
  const isPssOddsBoostMarketTypeCodesEnabled = useFeature(
    FeatureFlag.ISPReleasePssOddsBoostMarketTypeCodes
  );

  return (
    <>
      {isSingleOpportunity ? (
        <h4 className={classNames(styles.name, !isAvailableOpportunity && styles.isSuspended)}>
          {getRunnerNameWithHandicap({
            runnerName: opportunity.name,
            runnerResultType: isSingleAvailableOpportunity
              ? opportunity.runnerResultType
              : undefined,
            handicap: isAvailableOpportunity ? opportunity.handicap : 0
          })}
        </h4>
      ) : (
        <div className={styles.multipleNameWrapper}>
          <h4
            className={classNames(styles.name, {
              [styles.isSuspended]: !isAvailableOpportunity && !isSgmInvalidOpportunity
            })}
          >
            {getMultipleName({
              betType: opportunity.type as MultipleBetType,
              numberOfLines: opportunity.numberOfLines,
              isSgm: isSgmOpportunity
            })}
          </h4>

          <InfoTooltip parentRef={opportunityRef}>
            <MultiplesTooltipContent
              betType={opportunity.type as MultipleBetType}
              isSgm={isSgmOpportunity}
            />
          </InfoTooltip>
        </div>
      )}

      {isSingleAvailableOpportunity &&
        isOddsBoostMarket(isPssOddsBoostMarketTypeCodesEnabled, opportunity.marketType) && (
          <Icon
            name="price-boost"
            label={formatMessage(TXTWCL_odds_boost)}
            size="xl"
            className={styles.oddsBoostIcon}
          />
        )}

      <div className={styles.right}>
        {isAvailableOpportunity && opportunity.numberOfLines === 1 && (
          <OddsDisplay opportunity={opportunity} />
        )}

        {isSuspendedOpportunity && (
          <span className={styles.status}>
            <Translation {...TXTCLI_Suspended} />
          </span>
        )}

        {isSingleUnavailableOpportunity && (
          <span className={styles.status}>
            <Translation {...TXTWCL_Unavailable} />
          </span>
        )}

        {isSingleOpportunity && <RemoveButton runnerId={opportunity.runnerId} />}
      </div>
    </>
  );
};

export default Heading;
